import { useMemo, useRef } from 'react';

import set from 'lodash/set';

import {
  dateFNS,
  fromTimeZoneToUTC,
  toDate,
  toISOString,
} from '@gogame/intl-ts';

const SET_FIELD_VALUE = 'SET_FIELD_VALUE';
const SET_PERIOD_FIELD_VALUE = 'SET_PERIOD_FIELD_VALUE';
const REMOVE_PERIOD = 'REMOVE_PERIOD';
const ADD_PERIOD = 'ADD_PERIOD';

export function usePeriods({ value, onChange, createPeriod, updatePeriod }) {
  const dispatch = useMemo(
    () => getPeriodDispatch({ createPeriod, updatePeriod }),
    [createPeriod, updatePeriod]
  );
  const valueRef = useRef(null);
  valueRef.current = value;
  return {
    add: () => {
      const newValue = dispatch(valueRef.current, { type: ADD_PERIOD });
      onChange({}, newValue);
    },
    remove: (index) => {
      const newValue = dispatch(valueRef.current, {
        type: REMOVE_PERIOD,
        payload: { index },
      });
      onChange({}, newValue);
    },
    setFieldValue: (index, field, fieldValue) => {
      const newValue = dispatch(valueRef.current, {
        type: SET_FIELD_VALUE,
        payload: { index, field, value: fieldValue },
      });
      onChange({}, newValue);
    },
    setPeriodFieldValue: (index, field, fieldValue) => {
      const newValue = dispatch(valueRef.current, {
        type: SET_PERIOD_FIELD_VALUE,
        payload: { index, field, value: fieldValue },
      });
      onChange({}, newValue);
    },
  };
}

function getPeriodDispatch({ createPeriod, updatePeriod }) {
  return function periodDispatch(state, action) {
    const { type, payload } = action;
    switch (type) {
      case ADD_PERIOD: {
        const _period = state.slice(-1).pop()?.period;
        const _type = state.slice(-1).pop()?.activityType;
        const prevEndData =
          state.length && toDate(_period.to || _period.endDate);

        return [
          ...state,
          createPeriod({
            from: prevEndData
              ? toISO(
                  dateFNS.startOfDay(dateFNS.addDays(new Date(prevEndData), 1))
                )
              : toISO(dateFNS.startOfDay(new Date())),
            ...(_type && {
              activityType: _type,
            }),
          }),
        ];
      }
      case REMOVE_PERIOD: {
        const { index } = payload;

        const item = state[index];
        const next = state[index + 1];
        if (next) {
          const itemPeriod = item.period;
          const nextPeriod = next.period;
          const nextClone = updatePeriod(next, {
            from: itemPeriod.from || itemPeriod.startDate,
            to: nextPeriod.to || nextPeriod.endDate,
          });

          return [
            ...state.slice(0, index),
            nextClone,
            ...state.slice(index + 2),
          ];
        } else {
          return state.slice(0, -1);
        }
      }

      case SET_PERIOD_FIELD_VALUE: {
        const { index, field, value } = payload;
        let newState = changeByIndex(state, index, (item) => {
          return {
            ...item,
            period: {
              ...item.period,
              [field]: value,
            },
          };
        });

        const shouldNormalize = index < state.length - 1;
        if (shouldNormalize) {
          newState = normalizePeriods(newState, createPeriod);
        }

        return newState;
      }

      case SET_FIELD_VALUE: {
        const { index, field, value } = payload;
        return changeByIndex(state, index, (item) => {
          let obj = { ...item };

          return set(obj, field, value);
        });
      }
    }
  };
}

function toISO(value) {
  return toISOString(fromTimeZoneToUTC(value));
}

function changeByIndex(state, index, change) {
  return state.map((period, i) => {
    if (i === index) {
      return change(period);
    }
    return period;
  });
}

function normalizePeriods(
  value,
  createPeriod,
  isSupportActivityTypes,
  defaultActivityType
) {
  const periods = [value[0]];
  let changed = false;

  value.length > 1 &&
    value.reduce((prevPeriod, currPeriod) => {
      const prevEndData = toDate(
        prevPeriod.period.to || prevPeriod.period.endDate
      );
      const currStartData = toDate(
        currPeriod.period.from || currPeriod.period.startDate
      );
      const diff = dateFNS.differenceInDays(currStartData, prevEndData);

      if (diff === 1) {
        periods.push(currPeriod);
      } else if (diff > 1) {
        changed = true;
        periods.push(
          createPeriod({
            from: toISO(
              dateFNS.startOfDay(dateFNS.addDays(new Date(prevEndData), 1))
            ),
            to: currStartData
              ? toISO(
                  dateFNS.startOfDay(
                    dateFNS.subDays(new Date(currStartData), 1)
                  )
                )
              : undefined,
            isSupportActivityTypes,
            defaultActivityType,
          }),
          currPeriod
        );
      }

      return currPeriod;
    });

  return changed ? periods : value;
}
